/** Catalog for the API playground. Prefill site_id from the selected sidebar site. */

export const API_ENDPOINTS = [
  {
    id: "get_account",
    section: "Account",
    method: "GET",
    path: "/account/",
    label: "Get account",
    description: "Email, plan, and site/change limits for this API key.",
    fields: [],
  },
  {
    id: "list_sites",
    section: "Sites",
    method: "GET",
    path: "/sites/",
    label: "List sites",
    description: "List sites on this account with status, URLs, and draft_changes_count.",
    fields: [],
  },
  {
    id: "get_site",
    section: "Sites",
    method: "GET",
    path: "/sites/{site_id}/",
    label: "Get site",
    description:
      "Site detail including status, preview/live URLs, draft_changes_count, domain, and build_progress.",
    fields: [
      { key: "site_id", label: "Site id", source: "site", required: true },
    ],
  },
  {
    id: "create_site",
    section: "Sites",
    method: "POST",
    path: "/sites/",
    label: "Create site",
    description:
      "Create a site from a prompt. The plan is always auto-approved so the build starts when ready.",
    fields: [
      { key: "prompt", label: "Prompt", type: "textarea", required: true, body: true },
      { key: "auto_publish", label: "Auto-publish", type: "boolean", default: false, body: true },
    ],
  },
  {
    id: "update_site",
    section: "Sites",
    method: "PATCH",
    path: "/sites/{site_id}/",
    label: "Update site",
    description: "Update name, description, or discoverable. Publish afterward for settings to go live.",
    fields: [
      { key: "site_id", label: "Site id", source: "site", required: true },
      { key: "name", label: "Name", body: true },
      { key: "description", label: "Description", type: "textarea", body: true },
      { key: "discoverable", label: "Discoverable", type: "boolean", body: true },
    ],
  },
  {
    id: "set_domain",
    section: "Sites",
    method: "POST",
    path: "/sites/{site_id}/domain/",
    label: "Set custom domain",
    description: "Set or clear a custom domain. Pass an empty domain to clear. Returns DNS records.",
    fields: [
      { key: "site_id", label: "Site id", source: "site", required: true },
      { key: "domain", label: "Domain", body: true },
    ],
  },
  {
    id: "publish_site",
    section: "Sites",
    method: "POST",
    path: "/sites/{site_id}/publish/",
    label: "Publish site",
    description: "Publish the open draft, or republish live if there is no draft.",
    fields: [
      { key: "site_id", label: "Site id", source: "site", required: true },
    ],
  },
  {
    id: "list_changes",
    section: "Changes",
    method: "GET",
    path: "/sites/{site_id}/changes/",
    label: "List changes",
    description: "List change requests for a site.",
    fields: [
      { key: "site_id", label: "Site id", source: "site", required: true },
    ],
  },
  {
    id: "request_change",
    section: "Changes",
    method: "POST",
    path: "/sites/{site_id}/changes/",
    label: "Request change",
    description: "Queue a change. Set pre_approve true to publish when the draft is ready.",
    fields: [
      { key: "site_id", label: "Site id", source: "site", required: true },
      { key: "description", label: "Description", type: "textarea", required: true, body: true },
      { key: "pre_approve", label: "Auto-publish", type: "boolean", default: false, body: true },
    ],
  },
  {
    id: "get_change",
    section: "Changes",
    method: "GET",
    path: "/changes/{change_id}/",
    label: "Get change",
    description: "Get a change request by id.",
    fields: [
      { key: "change_id", label: "Change id", required: true },
    ],
  },
  {
    id: "cancel_change",
    section: "Changes",
    method: "POST",
    path: "/changes/{change_id}/cancel/",
    label: "Cancel change",
    description: "Cancel a queued or running change.",
    fields: [
      { key: "change_id", label: "Change id", required: true },
    ],
  },
  {
    id: "discard_change",
    section: "Changes",
    method: "POST",
    path: "/changes/{change_id}/discard/",
    label: "Discard change",
    description: "Discard a failed or canceled change.",
    fields: [
      { key: "change_id", label: "Change id", required: true },
    ],
  },
  {
    id: "retry_change",
    section: "Changes",
    method: "POST",
    path: "/changes/{change_id}/retry/",
    label: "Retry change",
    description: "Retry a failed or canceled change.",
    fields: [
      { key: "change_id", label: "Change id", required: true },
    ],
  },
];

export function resolveEndpointPath(endpoint, values, { usePlaceholders = false } = {}) {
  let path = endpoint?.path || "/";
  (endpoint?.fields || []).forEach((field) => {
    const token = `{${field.key}}`;
    if (!path.includes(token)) return;
    const raw = (values[field.key] || "").trim();
    if (raw) {
      path = path.replace(token, encodeURIComponent(raw));
    } else if (usePlaceholders) {
      path = path.replace(token, `{${field.key}}`);
    } else {
      path = path.replace(token, "");
    }
  });
  return path;
}

export function missingRequiredFields(endpoint, values) {
  return (endpoint?.fields || []).filter((field) => {
    if (!field.required) return false;
    if (field.type === "boolean") return false;
    return !(values[field.key] || "").toString().trim();
  });
}

export function placeholderForParam(key) {
  return `{${key}}`;
}
